import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Mail, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ResendConfirmation: React.FC = () => {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const redirectUrl = process.env.NODE_ENV === 'production'
        ? 'https://cevee.eu/auth/callback'
        : `${window.location.origin}/auth/callback`;

      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (resendError) {
        setError(resendError.message);
      } else {
        setSent(true);
      }
    } catch (err: any) {
      setError(err.message || t('resendConfirmation.errorResendFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-pink-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-pink-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('resendConfirmation.title')}
          </h1>
          <p className="text-gray-600">
            {t('resendConfirmation.description')}
          </p>
        </div>

        {!sent ? (
          <form onSubmit={handleResend} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('resendConfirmation.emailLabel')}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder={t('resendConfirmation.emailPlaceholder')}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-pink-600 via-pink-500 to-yellow-400 text-white py-3 rounded-lg font-medium hover:from-pink-700 hover:via-pink-600 hover:to-yellow-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('resendConfirmation.sendingButton') : t('resendConfirmation.sendButton')}
            </button>

            <Link
              to="/login"
              className="flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mt-4"
            >
              <ArrowLeft size={16} />
              {t('resendConfirmation.backToLogin')}
            </Link>
          </form>
        ) : (
          <div className="text-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <svg className="w-7 h-7 sm:w-10 sm:h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              {t('resendConfirmation.successTitle')}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              {t('resendConfirmation.successMessage')} <strong>{email}</strong> {t('resendConfirmation.successDescription')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-pink-600 hover:text-pink-700 font-medium transition-colors"
            >
              <ArrowLeft size={16} />
              {t('resendConfirmation.backToLogin')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResendConfirmation;

